import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { QuestionStore } from '../questions/QuestionStore'
import { action, observable } from 'mobx'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import { toStream } from '@/utils/mobx-util'
import { switchMap } from 'rxjs/operators'
import { withAbortSignal } from '@/utils/rx-util'
import type { Question } from '@/features/jobs/questions/questions'
import { ModalViewModel } from '@/infra/ModalViewModel'

/**
 * Manages the display of questions.
 */
export class QuestionsDisplayViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * State for when displaying as a modal.
   */
  readonly modal = new ModalViewModel()

  /**
   * The questions that were asked for this job.
   * When `null`, the questions haven't been fetched yet.
   */
  @observable accessor questions: Question[] | null = null

  constructor(
    public readonly job: SubmittedJob,
    private readonly _questionStore: QuestionStore,
  ) {
    // Fetch questions when active, and re-fetch when they change.
    this._lifecycle.manage(
      toStream(() => this.job.answers, true).pipe(
        switchMap(() =>
          withAbortSignal((signal) => this._fetchQuestions(signal)),
        ),
      ),
    )
  }

  activate() {
    this._lifecycle.activate()
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  private async _fetchQuestions(signal: AbortSignal) {
    await this._questionStore
      .fetchQuestions({
        jobId: this.job.id,
        signal,
      })
      .then(action((r) => (this.questions = r.questions)))
  }
}
